import { useState, useEffect, useRef } from 'react';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { sendWhatsAppWebhook } from '@/lib/notifications';
import { notifyOrderDelivering } from '@/lib/zapiWhatsApp';
import { markOrderDelivering, markOrderDelivered } from '@/lib/deliveryPin';
import DeliveryPinDialog from '@/components/DeliveryPinDialog';
import { formatCurrency } from '@/lib/formatters';
import { Badge } from '@/components/ui/badge';
import { Bike, MapPin, Phone, Navigation, CheckCircle, RefreshCw, UserCheck, Users, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { useKeepAlive } from '@/lib/keepAlive';
import { playDing, unlockAudio, installAutoUnlock } from '@/lib/sound';
import { updateOrderStatusOnServer, upsertDispatchOrder, fetchDispatchActive } from '@/lib/orderServerSync';
import { isTombstoned } from '@/lib/tombstones';

const DISPATCH_STATUSES = ['ready', 'delivering'];
const SOUND_STORAGE_KEY = 'kaven_entregador_sound';

export default function EntregadorPage() {
  const { operator } = useAuth();
  const [orders, setOrders] = useState([]);
  // v8.05 — keep-alive: tela ligada + heartbeat em Worker + resync ao acordar
  useKeepAlive(['deliveryOrders']);
  const [pinTarget, setPinTarget] = useState(null); // { order } quando dialog aberto
  // v7.68 — Alerta sonoro: novo pedido pronto / mudança de status remota.
  const knownStatusRef = useRef(null); // Map id→status, null = primeiro load
  const localActionAtRef = useRef(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(SOUND_STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  });

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try { localStorage.setItem(SOUND_STORAGE_KEY, String(next)); } catch {}
    unlockAudio(); // desbloqueia AudioContext com o gesto
    if (next) {
      playDing();
      toast.success('Alerta sonoro ativado');
    } else {
      toast('Alerta sonoro desligado');
    }
  };

  const load = async () => {
    // v7.94 — toArray + sort em JS (orderBy por índice excluía registros sem
    // createdAt indexável). Em produção raramente >50 entregas ativas.
    const all = await db.deliveryOrders.toArray();
    const myId = operator?.id;
    const filtered = all
      .filter((o) => DISPATCH_STATUSES.includes(o.status))
      // v7.94 — Pedido 'ready' SEMPRE visível pra todos os entregadores:
      // assignedTo residual (rota iniciada e revertida, ou id de operador
      // antigo pré-v7.89) escondia pedidos prontos de todo mundo.
      // Só 'delivering' respeita a atribuição (some pros outros).
      .filter((o) => o.status === 'ready' || o.assignedTo == null || o.assignedTo === myId)
      .sort((a, b) => {
        const mineA = a.assignedTo === myId ? 0 : 1;
        const mineB = b.assignedTo === myId ? 0 : 1;
        if (mineA !== mineB) return mineA - mineB;
        const na = (a.neighborhood || '').localeCompare(b.neighborhood || '');
        return na !== 0 ? na : new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      })
      .slice(0, 150);
    // v7.68 — ding em novo pedido pronto ou status alterado remotamente
    // (guard de 3s ignora as ações locais de startRoute/confirmDelivery).
    if (knownStatusRef.current !== null && Date.now() - localActionAtRef.current > 3000) {
      let newOnes = 0;
      let statusChanges = 0;
      for (const o of filtered) {
        const prev = knownStatusRef.current.get(o.id);
        if (prev === undefined) newOnes += 1;
        else if (prev !== o.status) statusChanges += 1;
      }
      if ((newOnes > 0 || statusChanges > 0) && soundEnabled) playDing();
      if (newOnes > 0) {
        toast.success(`🛵 ${newOnes} pedido${newOnes > 1 ? 's' : ''} pronto${newOnes > 1 ? 's' : ''} para entrega!`);
      }
    }
    knownStatusRef.current = new Map(filtered.map((o) => [o.id, o.status]));
    setOrders(filtered);
  };

  // v7.71 — desbloqueia o áudio no primeiro toque em qualquer lugar da página
  // (política de autoplay). Sem isso o ding ficava mudo até usar o toggle.
  useEffect(() => { installAutoUnlock(); }, []);

  // v7.68 — ignoreVisibility: entregador com tela bloqueada/app em segundo
  // plano continua verificando novos pedidos (quando o SO permitir timers).
  useVisiblePolling(load, 8000, { ignoreVisibility: true });

  // v8.07 — POLLING HTTP ATIVO (padrão do pagamento): puxa pedidos
  // ready/delivering DIRETO do backend por REST a cada 7s e reconcilia o
  // Dexie local — o pedido pronto chega mesmo com o socket Firebase morto.
  // Guardas: tombstones locais (nunca ressuscita apagado), status terminal
  // local nunca regride, LWW por updatedAt (+1s de folga).
  const reconcileFromServer = async () => {
    const remote = await fetchDispatchActive();
    if (!remote || !remote.length) return;
    let changed = 0;
    for (const rec of remote) {
      try {
        const o = rec.order || {};
        let id = o.id ?? rec.clientId;
        if (id === undefined || id === null) continue;
        if (typeof id === 'string' && /^\d+$/.test(id)) id = Number(id);
        if (await isTombstoned('deliveryOrders', id)) continue;
        const local = await db.deliveryOrders.get(id);
        if (local && ['delivered', 'cancelled'].includes(String(local.status))) continue;
        const rTs = new Date(rec.updatedAt || o.updatedAt || 0).getTime();
        const lTs = local ? new Date(local.updatedAt || local.createdAt || 0).getTime() : 0;
        if (!local || rTs > lTs + 1000) {
          await db.deliveryOrders.put({ ...o, id, status: rec.status });
          changed += 1;
        }
      } catch {}
    }
    if (changed > 0) {
      // eslint-disable-next-line no-console
      console.log(`[DISPATCH] ${changed} pedido(s) recuperado(s) via polling HTTP`);
      loadRef.current();
    }
  };
  // v8.09 — 30s e só com a tela visível: fallback HTTP (o tempo real segue
  // sendo o Firebase); 7s com N aparelhos afogava o backend de produção.
  useVisiblePolling(reconcileFromServer, 30000, {});

  // v7.73 — Tempo real: recarrega a lista na hora quando o Firebase mescla
  // mudanças de deliveryOrders no Dexie (sem F5). Ding via detecção no load.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const onMerge = (e) => {
      if (e?.detail?.table === 'deliveryOrders') loadRef.current();
    };
    window.addEventListener('kaven:remote-merge', onMerge);
    return () => window.removeEventListener('kaven:remote-merge', onMerge);
  }, []);

  // v7.81 -- Watchdog de 15s (paridade com KDS): religa os listeners de
  // deliveryOrders direto do Firebase caso o listener normal tenha morrido
  // silenciosamente (socket/erro). Roda mesmo em segundo plano.
  useEffect(() => {
    const id = setInterval(() => {
      import('@/lib/firebaseSync')
        .then(({ resyncTablesFromRemote, resyncBackendOrders }) => { resyncTablesFromRemote(['deliveryOrders']); resyncBackendOrders(); })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, []);

  // TICKET 207625 - Recuperacao em tempo real ao voltar/focar a tela (paridade
  // com a Cozinha). O setInterval de 15s e estrangulado em segundo plano e o
  // keepalive so reconecta o socket (nao religa listeners). Forcamos o resync
  // ao ficar visivel/focar - mesmo efeito do F5, sem recarregar.
  useEffect(() => {
    const forceResync = () => {
      import('@/lib/firebaseSync')
        .then(({ resyncTablesFromRemote, resyncBackendOrders }) => { resyncTablesFromRemote(['deliveryOrders']); resyncBackendOrders(); })
        .catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === 'visible') forceResync(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', forceResync);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', forceResync);
    };
  }, []);

  // v8.04 — PASSO 1: "Pegar Pedido" apenas ATRIBUI o pedido ao entregador.
  // Não abre mapa, não muda status, não notifica cliente — isso é do passo 2.
  const takeOrder = async (order) => {
    localActionAtRef.current = Date.now();
    const now = new Date().toISOString();
    await db.deliveryOrders.update(order.id, {
      assignedTo: operator?.id ?? null,
      assignedName: operator?.name || null,
      updatedAt: now,
    });
    try {
      const { pushToRemote } = await import('@/lib/firebaseSync');
      const updated = await db.deliveryOrders.get(order.id);
      if (updated) await pushToRemote('deliveryOrders', order.id, updated);
      // v8.07 — ponte HTTP: atribuição visível aos outros aparelhos via polling
      if (updated) upsertDispatchOrder(updated, updated.status || 'ready').catch(() => {});
    } catch {}
    toast.success(`Pedido #${order.id} é seu!`, {
      description: 'Toque em "Iniciar Rota" quando sair para a entrega.',
      duration: 4000,
    });
    load();
  };

  const buildMapsUrl = (order) => {
    const addressParts = [order.address, order.neighborhood, 'Mage - RJ'].filter(Boolean).join(', ');
    if (!addressParts) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressParts)}&travelmode=driving`;
  };

  // v8.04 — PASSO 2: "Iniciar Rota" é o ÚNICO momento que abre o Google Maps
  // e libera formalmente a rota (status delivering + PIN + avisos ao cliente).
  const startRoute = async (order) => {
    localActionAtRef.current = Date.now();
    const url = buildMapsUrl(order);
    if (!url) {
      toast.error('Endereço não informado');
      return;
    }
    // Pedido JÁ em rota: só reabre o mapa, sem repetir PIN/notificações.
    if (order.status === 'delivering') {
      try { window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
      return;
    }
    // v7.33 — Transição segura: gera PIN de 4 dígitos no helper.
    // TICKET 207625 - Abrir o mapa SINCRONAMENTE dentro do gesto de clique.
    // Antes, window.open() rodava DEPOIS dos awaits (markOrderDelivering /
    // sendWhatsAppWebhook), fora do gesto -> o bloqueador de popup do mobile
    // descartava a janela e "a rota nao abria". Abrimos ja aqui, no clique.
    let routeWin = null;
    try { routeWin = window.open(url, '_blank'); } catch {}
    try {
      // v7.35 — Gera/recupera o PIN no DB, mas NUNCA expõe pro entregador.
      // O cliente recebe via WhatsApp e vê na tela de rastreio.
      await markOrderDelivering(order.id, {
        assignedTo: operator?.id,
        assignedName: operator?.name,
      });
      const updated = await db.deliveryOrders.get(order.id);
      await sendWhatsAppWebhook(updated, 'delivering');
      // v7.72 — Progressão oficial no SERVIDOR (Mongo): 'delivering'.
      updateOrderStatusOnServer(updated, 'delivering').catch(() => {});
      // v8.07 — ponte HTTP de despacho: outros aparelhos veem via polling
      upsertDispatchOrder(updated, 'delivering').catch(() => {});
      // Fase 13 — Z-API REAL/MOCK: "Saiu para Entrega" com link de rastreio + PIN pro cliente
      notifyOrderDelivering(updated).catch((e) => console.warn('[Z-API] notifyOrderDelivering:', e));
      toast.success(`Rota aberta — peça o PIN ao cliente quando chegar`, {
        description: `Pedido #${order.id} · ${order.customerName || 'cliente'}`,
        duration: 5000,
      });
    } catch (err) {
      toast.error(err?.message || 'Falha ao iniciar rota');
      return;
    }
    // Fallback: popup bloqueado mesmo com o gesto (raro) -> tenta de novo.
    if (!routeWin || routeWin.closed) {
      try { window.open(url, '_blank', 'noopener,noreferrer'); } catch {}
    }
    load();
  };

  const requestDeliveryPin = (order) => {
    setPinTarget(order);
  };

  const confirmDelivery = async (pin) => {
    const order = pinTarget;
    if (!order) return;
    localActionAtRef.current = Date.now();
    try {
      await markOrderDelivered(order.id, pin, { deliveredBy: operator?.name });
    } catch (err) {
      // TICKET 207625 - Se o pedido estava STALE (ja entregue em outro device,
      // nao encontrado, ou ainda nao despachado localmente), reconcilia a lista
      // pra REMOVER o pedido-fantasma da tela em vez de deixa-lo travando a
      // operacao do entregador (caso do pedido #24). O erro segue pro dialog.
      const msg = err?.message || '';
      if (/entregue|saiu para entrega|encontrado/i.test(msg)) {
        try { load(); } catch {}
      }
      throw err;
    }
    // WhatsApp (mock local) + sync do servidor: fire-and-forget, nunca bloqueiam a baixa.
    sendWhatsAppWebhook({ ...order, status: 'delivered' }, 'delivered').catch(() => {});
    // v7.72 - Progressao oficial no SERVIDOR (Mongo): 'delivered'.
    updateOrderStatusOnServer(order, 'delivered').catch(() => {});
    // v8.07 — ponte HTTP: baixa definitiva no despacho (some do polling)
    upsertDispatchOrder({ ...order, status: 'delivered' }, 'delivered').catch(() => {});
    toast.success(`Pedido #${order.id} entregue!`);
    setPinTarget(null);
    load();
  };

  return (
    <div data-testid="entregador-page" className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-xl mx-auto">
        <header className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Entregas</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              {orders.length} pedido{orders.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="entregador-sound-toggle"
              onClick={toggleSound}
              title={soundEnabled ? 'Desligar alerta sonoro' : 'Ligar alerta sonoro'}
              className={`w-10 h-10 rounded-lg border flex items-center justify-center ${soundEnabled ? 'border-blue-300 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-400'} hover:bg-slate-50`}
            >
              {soundEnabled ? <Volume2 size={16} strokeWidth={1.75} /> : <VolumeX size={16} strokeWidth={1.75} />}
            </button>
            <button
              data-testid="entregador-refresh-btn"
              onClick={load}
              className="w-10 h-10 rounded-lg border border-slate-200 bg-white text-slate-500 flex items-center justify-center hover:bg-slate-50"
            >
              <RefreshCw size={16} strokeWidth={1.75} />
            </button>
          </div>
        </header>

        {orders.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <Bike size={48} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
            <p className="font-bold text-slate-700">Tudo entregue!</p>
            <p className="text-sm text-slate-500 mt-1">Novos pedidos prontos aparecerão aqui automaticamente.</p>
          </div>
        )}

        <div className="space-y-3">
          {orders.map((o) => {
            const isMine = o.assignedTo && o.assignedTo === operator?.id;
            const isUnassigned = o.assignedTo == null;
            return (
            <div
              key={o.id}
              data-testid={`entrega-${o.id}`}
              data-assigned={isMine ? 'mine' : isUnassigned ? 'free' : 'other'}
              className={`bg-white border rounded-2xl p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] ${
                isMine ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Pedido</span>
                    <span className="font-mono text-lg font-black text-slate-900">#{o.id}</span>
                    <Badge className={o.status === 'delivering' ? 'bg-purple-100 text-purple-700 border-purple-200 text-[10px]' : 'bg-amber-100 text-amber-700 border-amber-200 text-[10px]'}>
                      {o.status === 'delivering' ? 'A caminho' : 'Pronto'}
                    </Badge>
                    {isMine ? (
                      <Badge data-testid={`assigned-mine-${o.id}`} className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] flex items-center gap-1">
                        <UserCheck size={10} strokeWidth={2.25} /> Meu
                      </Badge>
                    ) : isUnassigned ? (
                      <Badge data-testid={`assigned-free-${o.id}`} className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] flex items-center gap-1">
                        <Users size={10} strokeWidth={2.25} /> Disponível
                      </Badge>
                    ) : null}
                  </div>
                  <p className="font-heading font-bold text-slate-900 truncate mt-1">{o.customerName || 'Cliente'}</p>
                </div>
                <p className="font-mono text-lg font-black text-slate-900">{formatCurrency(o.total || 0)}</p>
              </div>

              <div className="space-y-1 text-sm text-slate-600 mb-3">
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="font-bold text-slate-700">{o.neighborhood || 'Sem bairro'}</p>
                    {o.address && <p className="text-slate-500 text-xs">{o.address}</p>}
                    {o.landmark && <p className="text-[11px] text-slate-400 italic">Ref: {o.landmark}</p>}
                  </div>
                </div>
                {o.customerPhone && (
                  <a href={`tel:${o.customerPhone}`} className="flex items-center gap-2 text-blue-600 text-xs">
                    <Phone size={13} strokeWidth={1.75} /> {o.customerPhone}
                  </a>
                )}
              </div>

              <div className="flex gap-2">
                {o.status !== 'delivering' && isUnassigned ? (
                  <button
                    data-testid={`take-order-${o.id}`}
                    onClick={() => takeOrder(o)}
                    className="flex-1 h-12 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-700 active:translate-y-[1px] transition-all"
                  >
                    <UserCheck size={16} strokeWidth={1.75} /> Pegar Pedido
                  </button>
                ) : (
                  <button
                    data-testid={`start-route-${o.id}`}
                    onClick={() => startRoute(o)}
                    className={`flex-1 h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 active:translate-y-[1px] transition-all ${
                      o.status === 'delivering'
                        ? 'border-2 border-blue-500 text-blue-600 bg-white hover:bg-blue-50'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Navigation size={16} strokeWidth={1.75} /> {o.status === 'delivering' ? 'Reabrir Mapa' : 'Iniciar Rota'}
                  </button>
                )}
                <button
                  data-testid={`mark-delivered-${o.id}`}
                  onClick={() => requestDeliveryPin(o)}
                  disabled={o.status !== 'delivering'}
                  title={o.status === 'delivering' ? 'Confirmar entrega com PIN do cliente' : 'Inicie a rota antes de confirmar'}
                  className="h-12 px-4 rounded-xl border border-emerald-500 text-emerald-700 font-bold text-sm hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={16} strokeWidth={1.75} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
      <DeliveryPinDialog
        open={!!pinTarget}
        orderId={pinTarget?.id}
        customerName={pinTarget?.customerName}
        onClose={() => setPinTarget(null)}
        onConfirm={confirmDelivery}
      />
    </div>
  );
}
